/**
 * useUrlFilterState — Persist filter/search state in URL search params.
 *
 * Uses useState internally so updates don't trigger React Router re-renders
 * (which would reset dropdown open states). Syncs TO URL via replaceState
 * and reads FROM URL on initial mount only.
 */

import { useState, useCallback, useRef } from 'react';

interface FilterState {
  statuses: string[];
  assignees: string[];
  sortField: string;
  sortDir: 'ASC' | 'DESC';
  showDone: boolean;
  /** Only issues with a blocker that is not done */
  blockedOnly: boolean;
}

function readFiltersFromUrl(
  defaultSort: string,
  defaultDir: 'ASC' | 'DESC',
  defaultShowDone: boolean,
): FilterState {
  const params = new URLSearchParams(window.location.search);
  const statusesRaw = params.get('statuses');
  const assigneesRaw = params.get('assignees');
  return {
    statuses: statusesRaw ? statusesRaw.split(',').filter(Boolean) : [],
    assignees: assigneesRaw ? assigneesRaw.split(',').filter(Boolean) : [],
    sortField: params.get('sort') || defaultSort,
    sortDir: (params.get('dir') as 'ASC' | 'DESC') || defaultDir,
    showDone: params.has('done') ? params.get('done') === '1' : defaultShowDone,
    blockedOnly: params.get('blocked') === '1',
  };
}

function writeFiltersToUrl(
  filters: FilterState,
  defaultSort: string,
  defaultDir: 'ASC' | 'DESC',
  defaultShowDone: boolean,
): void {
  const params = new URLSearchParams(window.location.search);

  if (filters.statuses.length > 0) {
    params.set('statuses', filters.statuses.join(','));
  } else {
    params.delete('statuses');
  }
  if (filters.assignees.length > 0) {
    params.set('assignees', filters.assignees.join(','));
  } else {
    params.delete('assignees');
  }
  if (filters.sortField !== defaultSort) {
    params.set('sort', filters.sortField);
  } else {
    params.delete('sort');
  }
  if (filters.sortDir !== defaultDir) {
    params.set('dir', filters.sortDir);
  } else {
    params.delete('dir');
  }
  if (filters.showDone !== defaultShowDone) {
    params.set('done', filters.showDone ? '1' : '0');
  } else {
    params.delete('done');
  }

  if (filters.blockedOnly) {
    params.set('blocked', '1');
  } else {
    params.delete('blocked');
  }

  const qs = params.toString();
  const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(window.history.state, '', newUrl);
}

export function useUrlFilterState(
  defaultSort = 'updated',
  defaultDir: 'ASC' | 'DESC' = 'DESC',
  defaultShowDone = false,
): { filters: FilterState; setFilters: (updater: FilterState | ((prev: FilterState) => FilterState)) => void } {
  const [filters, setFiltersInternal] = useState<FilterState>(() =>
    readFiltersFromUrl(defaultSort, defaultDir, defaultShowDone),
  );

  const defaultsRef = useRef({ defaultSort, defaultDir, defaultShowDone });
  defaultsRef.current = { defaultSort, defaultDir, defaultShowDone };

  const setFilters = useCallback(
    (updater: FilterState | ((prev: FilterState) => FilterState)) => {
      setFiltersInternal((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const { defaultSort: ds, defaultDir: dd, defaultShowDone: dsd } = defaultsRef.current;
        writeFiltersToUrl(next, ds, dd, dsd);
        return next;
      });
    },
    [],
  );

  return { filters, setFilters };
}

/**
 * Simple text filter synced to URL param via replaceState.
 */
export function useUrlTextFilter(paramName = 'q'): [string, (value: string) => void] {
  const [value, setValueInternal] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get(paramName) || '';
  });

  const paramRef = useRef(paramName);
  paramRef.current = paramName;

  const setValue = useCallback(
    (newValue: string) => {
      setValueInternal(newValue);
      const params = new URLSearchParams(window.location.search);
      if (newValue.trim()) {
        params.set(paramRef.current, newValue);
      } else {
        params.delete(paramRef.current);
      }
      const qs = params.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState(window.history.state, '', newUrl);
    },
    [],
  );

  return [value, setValue];
}

/**
 * Numeric page param synced to URL via replaceState.
 */
function sanitizePage(n: number): number {
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}

export function useUrlPage(): [number, (page: number) => void] {
  const [page, setPageInternal] = useState<number>(() => {
    const params = new URLSearchParams(window.location.search);
    return sanitizePage(Number(params.get('page')));
  });

  const setPage = useCallback(
    (requested: number) => {
      const newPage = sanitizePage(requested);
      setPageInternal(newPage);
      const params = new URLSearchParams(window.location.search);
      if (newPage > 1) {
        params.set('page', String(newPage));
      } else {
        params.delete('page');
      }
      const qs = params.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState(window.history.state, '', newUrl);
    },
    [],
  );

  return [page, setPage];
}
